import React, { useState, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import { useChatData, useChatClient } from '../context/ChatContext';
import { isValidEthAddress } from '../utils/chatHelpers';
import { resolveHnsName } from '../../../api/mcpApi';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

interface NewConversationModalProps {
  show: boolean;
  onHide: () => void;
}

const NewConversationModal: React.FC<NewConversationModalProps> = ({ show, onHide }) => {
  const { t } = useTranslation();
  const { createDm, selectConversation } = useChatData();
  const [address, setAddress] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [resolvedInfo, setResolvedInfo] = useState<string | null>(null);
  const [error, setError] = useState('');

  const handleCreate = useCallback(async () => {
    setError('');
    setResolvedInfo(null);

    const input = address.trim();
    if (!input) {
      setError(t('chat.enterAddress', 'Please enter a wallet address or @username'));
      return;
    }

    let peerAddress = input;

    // If not a raw 0x address, resolve via HNS / name services
    if (!isValidEthAddress(input)) {
      setIsResolving(true);
      try {
        const result = await resolveHnsName(input);
        if (!result?.address) {
          setError(t('chat.usernameNotFound', 'Username not found'));
          setIsResolving(false);
          return;
        }
        peerAddress = result.address;
        setResolvedInfo(`${input} → ${peerAddress.slice(0, 6)}...${peerAddress.slice(-4)}`);
      } catch (err: any) {
        setError(err?.response?.data?.message || t('chat.usernameNotFound', 'Username not found'));
        setIsResolving(false);
        return;
      }
      setIsResolving(false);
    }

    setIsCreating(true);
    try {
      const conversationId = await createDm(peerAddress);
      if (conversationId) {
        selectConversation(conversationId);
        setAddress('');
        setResolvedInfo(null);
        onHide();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to start conversation');
    } finally {
      setIsCreating(false);
    }
  }, [address, createDm, selectConversation, onHide, t]);

  const handleClose = () => {
    setAddress('');
    setError('');
    setResolvedInfo(null);
    onHide();
  };

  const isProcessing = isCreating || isResolving;

  return (
    <Modal show={show} onHide={handleClose} centered className="chat-modal">
      <Modal.Header closeButton>
        <Modal.Title>{t('chat.newConversation', 'New Conversation')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="chat-modal__field">
          <label className="chat-modal__label">
            {t('chat.recipientLabel', 'Recipient Address or @username')}
          </label>
          <input
            type="text"
            className="chat-search-input"
            placeholder="0x... or @username"
            value={address}
            onChange={(e) => {
              setAddress(e.target.value);
              setError('');
              setResolvedInfo(null);
            }}
            onKeyDown={(e) => e.key === 'Enter' && !isProcessing && handleCreate()}
            autoFocus
          />
          {resolvedInfo && (
            <p className="chat-modal__resolved">{resolvedInfo}</p>
          )}
          {error && <p className="chat-modal__error">{error}</p>}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="chat-btn chat-btn--secondary" onClick={handleClose}>
          {t('chat.cancel', 'Cancel')}
        </button>
        <button
          className="chat-btn chat-btn--primary"
          onClick={handleCreate}
          disabled={isProcessing || !address.trim()}
        >
          {isProcessing ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin />{' '}
              {isResolving
                ? t('chat.resolving', 'Resolving...')
                : t('chat.creating', 'Creating...')}
            </>
          ) : (
            t('chat.startChat', 'Start Chat')
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default NewConversationModal;
